import { getSupabase } from './supabase';
import type { StaffMember, StaffRole } from './types';

/**
 * Roster changes that need more authority than the person at the till has.
 *
 * Creating a login means creating an auth account with a password somebody
 * else chose, and only the service role can do that. That key would be readable
 * by anyone who opened the app bundle, so it lives in the `manage-staff` edge
 * function instead and these are the calls into it. The function re-checks
 * ownership against the caller's own token — being able to reach it is not the
 * same as being allowed to use it.
 *
 * All three are online-only. Unlike an order, a login cannot be queued in the
 * outbox and settled later: the worker is standing there waiting to sign in,
 * and a password that only exists on one tablet is worse than an error.
 */
/**
 * Thrown when the request never reached the server. Named so the screen can
 * say "you are offline" rather than showing a fetch error to somebody whose
 * only problem is the shop's wifi.
 */
export const OFFLINE_ERROR = 'OfflineError';

async function callManageStaff<T>(body: Record<string, unknown>): Promise<T> {
  const sb = getSupabase();
  if (!sb) throw new Error('No backend configured');

  let data: unknown;
  let error: { name?: string; message: string } | null;
  try {
    ({ data, error } = await sb.functions.invoke('manage-staff', { body }));
  } catch (e) {
    const offline = new Error(e instanceof Error ? e.message : String(e));
    offline.name = OFFLINE_ERROR;
    throw offline;
  }

  // A non-2xx reply carries our own message in the body, which is far more use
  // than "Edge Function returned a non-2xx status code".
  if (error) {
    const detail = (data as { error?: string } | null)?.error;
    if (!detail && /fetch|network/i.test(error.message)) {
      const offline = new Error(error.message);
      offline.name = OFFLINE_ERROR;
      throw offline;
    }
    throw new Error(detail || error.message);
  }
  if (data && typeof data === 'object' && 'error' in data) {
    throw new Error(String((data as { error: string }).error));
  }
  return data as T;
}

export interface NewStaffAccount {
  restaurantId: string;
  email: string;
  password: string;
  role: StaffRole;
  active?: boolean;
}

export interface CreatedStaff {
  member: StaffMember;
  /**
   * That address already had a login — they work at another shop, or were on
   * this roster before. They keep the password they already have, and the one
   * the owner just typed is discarded rather than overwriting it.
   */
  reusedExistingLogin: boolean;
}

/** Adds somebody to the roster and gives them a login, in one step. */
export async function createStaffAccount(input: NewStaffAccount): Promise<CreatedStaff> {
  const res = await callManageStaff<{ member: StaffRow; reusedExistingLogin?: boolean }>({
    action: 'create',
    restaurantId: input.restaurantId,
    email: input.email.trim().toLowerCase(),
    password: input.password,
    role: input.role,
    active: input.active !== false,
  });
  return {
    member: toStaffMember(res.member),
    reusedExistingLogin: res.reusedExistingLogin === true,
  };
}

/**
 * Replaces somebody's password with one the owner has chosen. There is no way
 * to read the existing one; forgetting it means being given a new one.
 */
export async function setStaffPassword(staffId: string, password: string): Promise<void> {
  await callManageStaff<{ ok: true }>({ action: 'set_password', staffId, password });
}

/** Removes the membership, and the login too once it opens nothing else. */
export async function deleteStaffAccount(staffId: string): Promise<void> {
  await callManageStaff<{ ok: true }>({ action: 'delete', staffId });
}

interface StaffRow {
  id: string;
  restaurant_id: string;
  user_id: string | null;
  email: string;
  role: string;
  active: boolean;
}

function toStaffMember(row: StaffRow): StaffMember {
  return {
    id: row.id,
    restaurantId: row.restaurant_id,
    userId: row.user_id,
    email: row.email,
    role: row.role === 'owner' ? 'owner' : 'worker',
    active: row.active !== false,
  };
}
